import React from "react";
import { Link } from "react-router-dom";
import { styles } from "../../layout";

const Thesis: React.FC = () => {
  return (
    <div style={styles.pageContainer}>
      <h1 style={styles.header}>How to write a Thesis</h1>

      <p style={styles.paragraph}>
        In December of 2024, I wrote my BPhil Honors thesis to graduate from the
        University of Pittsburgh. Writing my thesis was one of the hardest
        academic challenges I have ever taken on. It was uncommon for a business
        student (I studied Finance) to write a thesis and I wrote it with the
        goal of improving my writing skills. I will forever be grateful for the
        experience as it forced me to develop a writing method and create a
        piece of work from start to finish. In an attempt to reflect on the
        experience, I want to go over things I wish I knew before I started.
      </p>

      <h2 style={styles.subHeader}>Organization</h2>

      <p style={styles.paragraph}>
        Organization is key to writing a thesis. Before the writing process even
        begins, you are researching, organizing, and sorting a large amount of
        data, quotes, and information. Having a consistent and sophisticated
        organization system is key to being efficient with your time and
        developing your ideas.
      </p>

      <p style={styles.paragraph}>
        A few years before my thesis, I had learned about the concept of a
        “second brain” which was a way to store and collect information to
        reduce the load of your first brain or actual brain. This led me to
        explore online notetaking applications, and I eventually fell in-love
        with notion. Despite my 2 year experience with notion I decided to do my
        prelimiary research in google docs as for some reason it felt more
        manageable. This was my first organizational mistake. Although I
        eventually moved over to notion about 50 days in (50 days of making a
        new doc everyday) my system could have been much better if I had
        committed to an organization system and stuck with it.
      </p>

      <p style={styles.paragraph}>
        There are countless ways to organize your thesis writing process. I
        would encourage you to choose one that works with you and that you are
        familiar with. If you like writing things in a journal or by hand, by
        all means, use a system you are comfortable with. I recommend exploring
        notion as it is designed for these types of projects.
      </p>

      <h2 style={styles.subHeader}>Time Management</h2>

      <p style={styles.paragraph}>
        Managing your time is key when meeting harsh deadlines and working on a
        project as large as a thesis. To further clarify I will break down time
        management into a daily and extended time periods.{" "}
      </p>

      <h3 style={styles.subSubHeader}>Daily Time Management</h3>

      <p style={styles.paragraph}>
        It is important to have daily rituals that keep you on track and using
        your time efficiently. Luckily my thesis fell upon my final semester, so
        my class schedule was very conducive to large time blocks of deep work
        in the morning. I did not have a class before 12pm anyway during the
        week. In order to maximize my daily time I would aim to wake up as early
        as I could (around 6am) to immediately start working. I would try to get
        as much done as I could before my first class.
      </p>

      <p style={styles.paragraph}>
        This practice was beneficial as almost everyday I would have at least a
        few hours of good work in the morning, but I was still not optimized in
        my approach. I would not take break during this time, just keep pushing
        to get as much work done as possible. I was always rushing to eat and
        get to class before 12pm as I would not take breaks for meals or to get
        ready, and I rarely was prepared for the rest of the day as I did
        nothing but thesis work in the morning.
      </p>

      <p style={styles.paragraph}>
        In order to improve I would have adopted a pomodoro style routine where
        I had intense periods of focus followed by short breaks where I could do
        other things, like eat or just relax to ensure I was prepared.{" "}
      </p>

      <h3 style={styles.subSubHeader}>Extended Time Management</h3>

      <p style={styles.paragraph}>
        Deadlines are key in a large project like this. Breaking up the project
        into chunks and completing tasks in a planned sequentional order keeps
        you on track. Luckily the honors college had a timeline in which certain
        procedures had to be completed, for example a deadline for first draft,
        defense data, and final submission. This allowed me to stay on track and
        prioritize tasks based on deadlines.
      </p>

      <p style={styles.paragraph}>
        If your writing outside of a program or you don’t have strict defined
        deadlines like this consider creating your own and sticking to it. It
        might take you one hour upfront to create a plan and will save you in
        the long run as you know what to prioritize.
      </p>

      <p style={styles.paragraph}>
        Long term time management is essential for a project of this size.{" "}
      </p>

      <h2 style={styles.subHeader}>Talk to people</h2>

      <p style={styles.paragraph}>
        This tip is somewhat abstract as it relates to many aspect of the thesis
        process. First and most importantly talk to relevant people about the
        process and what is required. Over a year and a half before I submitted
        my application I had spoken to someone in the honors college about the
        BPhil and what was required. Leading up to my prospectus application I
        spoke to the program manager about my topic and if the program would be
        a good fit. These conversation gave me insight to what would be required
        and if I was a good candidate for the program.
      </p>

      <p style={styles.paragraph}>
        Besides talking to faculty about the thesis itself it is key to talk to
        industry leaders and experts relevant to your topic. In another blog
        (How to land a job) I mention 50+ networking calls I had had with people
        in the sustainable finance industry. This gave me a huge insight into
        sustainability and developed my interest in networking and
        conversations. Durning my thesis I reached out to a professor whose
        paper I quoted and he was willing to talk to me about my experiment
        design and thesis as a whole. Leaning on industry experts allows you to
        clarify and ask questions and get direction with your research. Reach
        out to others at your institution as well as anyone you can find online.
      </p>

      <p style={styles.paragraph}>
        Finally talk to your advisor and other committee members as well as
        people close to you. Just speaking about your ideas and seeing what
        resonates with others is a great way to see what ideas are worth
        pursuing. Early on my advisor heard all my ideas and pushed me to narrow
        my focus. This was a pivotal moment as I went from thinking the task
        ahead was impossible to feeling confident I could cover the material. It
        also ended up allowing me to dive deeper and explore my topic further.{" "}
      </p>

      <h2 style={styles.subHeader}>Effort</h2>

      <p style={styles.paragraph}>
        Effort is what separates good from great. Almost anyone can go through
        the motions and write something, but to write something truly great
        requires maximum effort. For me, I have noticed when I truly want to do
        something I put in a significantly higher amount of effort than on
        something that is assigned to me, even if I find it interesting. So try
        to pursue things you truly want to do in order to increase your minimum
        threshold for effort.
      </p>

      <p style={styles.paragraph}>
        A reoccurring thought for me that allowed me to continue to put effort
        in even when I was tired and burnt out was that this was my work. As I
        mentioned before this would be the first piece of work that I would be
        able to look back on to compare my writing. Therefore I wanted to put my
        best for forward to showcase my true abilities. The reminder that this
        was my name on the document and a chance to show my skills allowed to
        put more effort into this project than any other document.{" "}
      </p>

      <p style={styles.paragraph}>
        I truly believe that I put as much effort as I could into my thesis and
        I saw how hard I can work, and my next piece of writing I will put that
        amount of effort plus some.{" "}
      </p>

      <h2 style={styles.subHeader}>Writing Skills</h2>

      <p style={styles.paragraph}>
        My thesis ended up being 54 pages and over 9000 words. This was the
        longest thing I have written to date and I learned a lot about writing
        throughout the process. The reason I started the thesis was to improve
        my writing skills and I can feel the difference after writing it. I feel
        much more comfortable writing and enjoy it.
      </p>

      <p style={styles.paragraph}>
        First, I developed a writing process. Before I began writing anything
        thesis related I started a challenge of “100 Days of Writing”. The goal
        was simple, start a new blank page everyday and write for 1 hour. While
        I did not complete 100 days in their entirely, the simple process of
        opening up a new document daily and pushing myself to write for 1 hour
        improved my text output skills, something that is an immediate cure for
        writers block. Getting words on the page was vital to my success.
      </p>

      <p style={styles.paragraph}>
        Sometimes I would give myself a prompt of if there was something I was
        struggling with I would spend the full hour working on it. For example,
        I spent alot of time explaining and creating my outline. What sections
        would go where an why, just writing out my thoughts and explanations.
        This allowed me to create an outline that had reasoning behind it, and
        was worth the initial investment.
      </p>

      <p style={styles.paragraph}>
        Sometimes I would give myself a prompt of if there was something I was
        struggling with I would spend the full hour working on it. For example,
        I spent alot of time explaining and creating my outline. What sections
        would go where an why, just writing out my thoughts and explanations.
        This allowed me to create an outline that had reasoning behind it, and
        was worth the initial investment.
      </p>

      <p style={styles.paragraph}>
        After the initial “word dump” or “text output stage” I would sift
        through the ideas for anything useful. Then I would bring it over to my
        outline document and past it under the corresponding section. I would
        then fill in the gaps with more text output until I had large chunks of
        ideas under each section. This is where the next phase would begin,
        editing.
      </p>

      <p style={styles.paragraph}>
        Editing for me happened in multiple phases. First would be to just try
        to make sense of the text I had organized under each section. It was
        still clearly in word dump format and needed to be cleaned up and taken
        out unecesary sentances. Many times I would just rewrite the section
        entirley and keep the main ideas but reword them. Then, I would go back
        section by section and use a tool like Grammarly or Hemingway to really
        pick through each sentence to simplify ideas.
      </p>

      <p style={styles.paragraph}>
        This was the process to get the first draft.
      </p>

      <p style={styles.paragraph}>
        Text Output → Organize → Rewrite → Edit with Tools
      </p>

      <p style={styles.paragraph}>
        After that I would print out pages at a time and read them out loud and
        make marks on the page. Then go back through digitally and try to make
        sense of the marks I had made. I repeated this process a few times,
        making sure I could read sections outline and they made sense and felt
        smooth.
      </p>

      <p style={styles.paragraph}>
        That has became my process for almost any piece of writing. Allowing
        myself freedom for the first phase before I even consider it a draft has
        been liberating and allowed me to focus on pure writing before I
        constrain myself to a style or coherent structure. The messy
        organization of ideas before another draft allows me to play with
        different section order or sentence order. The harsh reading out loud
        allows each word to be tested for its use and anything that can be
        simplified is crossed out.
      </p>

      <h2 style={styles.subHeader}>Final Thoughts</h2>

      <p style={styles.paragraph}>
        A thesis is a great way to pursue something of interest, come up with an
        idea, defend it with rigorous research, and develop your writing skills.
        I enjoyed the process immensely and am grateful that I pushed myself to
        do it.
      </p>

      <p style={styles.paragraph}>
        I hope you learned something and found this valuable!
      </p>

      <p>- Jack</p>
      <p style={styles.date}>Published on July 7, 2025</p>
      <Link to="/blogs" style={styles.backLink}>
        Back to Blogs
      </Link>
    </div>
  );
};

export default Thesis;
